import { History } from 'history';
import { OptionType } from '@fullstory/components';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { SEGMENT_SEARCH_ROUTE, generateRoutePath, ParamsFrom } from '@fullstory/routes';
import { SegmentOption } from '../components/SegmentSelect';
import { intl } from '@fullstory/intl';
import { LibraryObject } from '../components';
import { LibraryCardItemType } from '../types';
import moment from 'moment';

export const getEmptyCustomSegment = (segment: SegmentSettings | null): SegmentDetail => {
  const segmentDefinition =
    segment?.segmentDefinition || segment?.namedSegmentDefinition?.segmentDefinition || null;
  const name =
    segment?.namedSegmentDefinition?.name ||
    intl.formatMessage({ defaultMessage: 'Custom Segment', id: 'f7ef5f' });
  return {
    segment: {
      segment: {
        id: '',
        name,
        description: '',
        segmentDefinition,
      },
      segmentMetadata: null,
      metadata: null,
    },
    starred: false,
    lastViewed: null,
  };
};

export const segmentToLink = (orgId: string) => (segment: SegmentDetail) => {
  const segmentId = segment?.segment?.segment?.id;
  if (!segmentId || isCustomSegment(segment)) return '';
  const params: ParamsFrom<typeof SEGMENT_SEARCH_ROUTE> = {
    search: ':search',
    segmentId,
    orgId,
    viewMode: 'people',
  };
  return generateRoutePath(SEGMENT_SEARCH_ROUTE, params);
};

export const navigateToSegment =
  (history: History, orgId: string, handleCustomSegment?: (segment: SegmentDefinition) => void) =>
  (opt: OptionType<SegmentOption>) => {
    const segmentId: string | undefined = opt.value.id;
    const segmentDefinition = opt.value.definition;
    if (!segmentId && segmentDefinition && handleCustomSegment)
      handleCustomSegment(segmentDefinition);
    else if (segmentId) {
      history.push(
        generateRoutePath(SEGMENT_SEARCH_ROUTE, {
          orgId,
          segmentId,
          viewMode: 'people',
        }),
      );
    }
  };

const ARCHIVED_SUFFIX = intl.formatMessage({ defaultMessage: '(archived)', id: 'JdiOEy' });
export const getSelectedItemLabel = (selectedItem: { name: string; archived: boolean }) =>
  `${selectedItem.name}${selectedItem.archived ? ` ${ARCHIVED_SUFFIX}` : ''}`;

export const getCardTooltipValue = (value: number): string => {
  if (isFinite(value)) {
    return intl.formatNumber(value, { minimumFractionDigits: 0, maximumSignificantDigits: 2 });
  }
  return intl.formatMessage({
    defaultMessage: '--',
    id: '50+smM',
    description: 'Output to user when formatting an invalid number.',
  });
};

export const isCustomSegment = (segment: SegmentDetail | null | undefined): boolean =>
  // custom segments have an `Id` used for the key and it always starts with `custom_segment`
  !!segment?.segment?.segment?.id?.startsWith('custom_segment');

export const isCustomSegmentById = (segmentId: string | undefined): boolean =>
  // custom segments either start with `custom_segment` or have no id
  !!segmentId?.startsWith('custom_segment') || !segmentId;

export const customSegmentIdToName = (segmentId: string): string =>
  // only a handful of custom segments support real names. This is a small drop in function that
  // will convert the id to a human readable name under the current naming constraints.
  segmentId.replace('custom_segment', 'Custom Segment').replace('_', ' ');

/**
 * Type guard that checks if the object is a LibraryObject using the objectType param
 * @param object
 * @returns true if the object is a LibraryObject
 */
export const isLibraryObject = (
  object: LibraryObject | LibraryCardItemType | undefined,
): object is LibraryObject => (object as LibraryObject)?.objectType !== undefined;

/**
 * Type guard that checks if the object is a LibraryCardItemType using the type param
 * @param object
 * @returns true if the object is a LibraryCardItemType
 */
export const isLibraryItemType = (
  object: LibraryObject | LibraryCardItemType | undefined,
): object is LibraryCardItemType => (object as LibraryCardItemType)?.type !== undefined;

/**
 * Returns a file name given an object name and extension. Spaces will be replaced with underscores.
 * @param objectName name of the object.
 * @param extension extension of the file name
 * @returns a consistently formatted file name
 */
export function getFileNameForObject(objectName: string, extension: string) {
  return `${objectName.replace(/ /g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.${extension}`;
}
